import { Box, Card, Flex, Select, Stack } from '@mantine/core';
import { SwitchInput, SelectInput, NumberInput } from 'api/actions/get-sensor/get-sensor-response';
import { Bar, Line } from 'react-chartjs-2';
import H1Medium from 'theme/components/Typography/H1Medium';
import P2Medium from 'theme/components/Typography/P2Medium';
import P4Medium from 'theme/components/Typography/P4Medium';
import P4Regular from 'theme/components/Typography/P4Regular';

import {
  Chart as ChartJS,
  TooltipItem,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
  Chart,
} from 'chart.js';

ChartJS.register(BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Title);

interface SensorStatisticsProps {
  calculateAverageValue: string | number;
  calculateAggregateValue: string | number;
  sensor: {
    id?: number;
    name?: string;
    mainBox?: {
      icon?: string;
      value?: number | null;
      unit?: string;
      headline?: string;
      subHeadline?: string;
    };
    alerts?: {
      active?: boolean;
      title?: string;
      options?: (SwitchInput | SelectInput | NumberInput)[];
    }[];
    chart?: {
      graphType?: 'bar' | 'line';
      title?: string;
      views?: {
        title?: string;
        range?: string;
        data?: { labelShort?: string; labelLong?: string; value?: number; unit?: string }[];
      }[];
    };
  };
  getRangeForSelectedTitle: (title: string) => string | undefined;
  selectedTitle: string | null;
  setSelectedTitle: (value: string | null) => void;
  titles: (string | undefined)[];
  chartData: {
    labels: (string | undefined)[];
    datasets: {
      barPercentage: number;
      label: string;
      data: number[];
      backgroundColor: string;
      hoverBackgroundColor: string;
      borderRadius: number;
      borderSkipped: boolean;
    }[];
  } | null;
  chartOptions:
    | {
        scales: {
          x: {
            ticks: { color: string | undefined };
            grid: { drawBorder: boolean; display: boolean };
            border: { display: boolean };
          };
          y: {
            ticks: { color: string | undefined };
            grid: { drawBorder: boolean; display: boolean };
          };
        };
        plugins: {
          tooltip: {
            enabled: boolean;
            callbacks: { title: (context: TooltipItem<any>[]) => string; label: (context: TooltipItem<any>) => string };
          };
          legend: { display: boolean };
        };
      }
    | {
        pointBackgroundColor: string;
        pointBorderColor: string;
        pointBorderWidth: number;
        pointRadius: number;
        pointHoverRadius: number;
        fill: boolean;
        elements: {
          line: {
            borderWidth: number;
            tension: number;
            borderColor: string;
          };
        };
        scales: {
          x: {
            ticks: { color: string | undefined };
            grid: { drawBorder: boolean; display: boolean };
            border: { display: boolean };
          };
          y: {
            ticks: { color: string | undefined };
            grid: { drawBorder: boolean; display: boolean };
          };
        };
        plugins: {
          tooltip: {
            enabled: boolean;
            callbacks: { title: (context: TooltipItem<any>[]) => string; label: (context: TooltipItem<any>) => string };
          };
          legend: { display: boolean };
        };
      };
  backgroundBar:
    | {
        id: string;
        beforeDraw: (chart: Chart<'bar'>) => void;
      }
    | {
        id: string;
        beforeDatasetsDraw: (chart: Chart<'line'>) => void;
      };
  _t: (msgid: string) => string;
}

/**
 * A component that displays sensor statistics.
 *
 * @prop {function} calculateAverageValue - A function that calculates the average value of the sensor.
 * @prop {object} sensor - The sensor object containing the data.
 * @prop {function} getRangeForSelectedTitle - A function that returns the range of the selected title.
 * @prop {string | null} selectedTitle - The selected title.
 * @prop {function} setSelectedTitle - A function that sets the selected title.
 * @prop {string[]} titles - An array of titles.
 * @prop {object | null} chartData - The chart data.
 * @prop {object} chartOptions - The chart options.
 * @prop {object} backgroundBar - The background bar.
 */
export default function SensorStatistics({
  calculateAverageValue,
  calculateAggregateValue,
  sensor,
  getRangeForSelectedTitle,
  selectedTitle,
  setSelectedTitle,
  titles,
  chartData,
  chartOptions,
  backgroundBar,
  _t,
}: SensorStatisticsProps) {
  const graphType = sensor?.chart?.graphType;
  const statisticValue = graphType === 'bar' ? calculateAggregateValue : calculateAverageValue;
  const statisticUnit = statisticValue === '—' ? '' : sensor?.mainBox?.unit;
  // console.log(graphType);

  return (
    <Stack gap={16}>
      <P2Medium color="gray.7">{_t('Statistics')}</P2Medium>
      <Card p={20} radius={10}>
        <Flex gap={12} mb={20}>
          <Box w="50%">
            <P4Regular color="gray.7">{sensor.chart?.title}</P4Regular>
            <H1Medium>
              {statisticValue}
              {statisticUnit}
            </H1Medium>
            <P4Medium>{getRangeForSelectedTitle(selectedTitle || '')}</P4Medium>
          </Box>
          <Box w="50%">
            <Select
              variant="tertiary"
              radius={10}
              styles={{ input: { border: '1px solid #E0E4EC', height: '40px' } }}
              value={selectedTitle}
              onChange={(value) => setSelectedTitle(value)}
              placeholder={_t('Select time range')}
              data={titles
                .filter((title): title is string => title !== undefined)
                .map((title) => ({
                  value: title,
                  label: _t(title),
                }))}
              nothingFoundMessage={_t('No options')}
              searchable
            />
          </Box>
        </Flex>

        {chartData ? (
          graphType === 'bar' ? (
            <Bar
              data={chartData}
              options={chartOptions}
              plugins={[backgroundBar as { id: string; beforeDraw: (chart: Chart<'bar'>) => void }]}
            />
          ) : (
            <Line
              data={chartData}
              options={chartOptions}
              plugins={[backgroundBar as { id: string; beforeDatasetsDraw: (chart: Chart<'line'>) => void }]}
            />
          )
        ) : (
          <p>{_t('No data available')}</p>
        )}
      </Card>
    </Stack>
  );
}
